import { type DatasourceType, type Access } from '@atlaskit/linking-types';
import { getTextFromDatasourceValue } from '@databases/content-api/confluence/utils';
import { type AsyncOperation, AsyncOperationStatus } from '@databases/redux-types';
import { type SerializedError } from '@databases/types';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { type DatasourceType as ConnectionType } from '@canvas/atlassian-service/datasource';

export type DatabaseSpecificValueType = PageStatusType | HtmlType;

export type FieldValueTypes = DatasourceType | DatabaseSpecificValueType;

export interface ColumnSchema {
  key: string;
  title: string;
  type: FieldValueTypes['type'];
  isList?: boolean;
}

export type ConnectionColumns = {
  [key: string]: ColumnSchema;
};

export type Connection = {
  cloudId: string;
  type: ConnectionType;
  requiredColumns: string[];
  content: string[];
  columns: ConnectionColumns;
  meta:
    | {
        access: string;
      }
    | undefined;
};

export type PageStatusType = {
  type: 'pageStatus';
  value: {
    text: string;
    color: string;
  };
};

export type HtmlType = {
  type: 'html';
  value: 'string';
};

// Enhanced version of DatasourceDataResponseItem.
export type ContentItem = {
  type: FieldValueTypes['type'];
  text: string;
  data: FieldValueTypes['value'] | FieldValueTypes['value'][];
};

export type ContentItems = {
  [key: string]: ContentItem;
};

export type Content = {
  connectionId: string;
  ari: string;
  item: ContentItems;
};

export type ConnectionsState = {
  connections: { [connectionId: string]: Connection };
  status: {
    [ari: string]: {
      content: AsyncOperation<void>;
      columns?: {
        [column: string]: AsyncOperation<void>;
      };
    };
  };
  content: { [ari: string]: Content };
};

export const initialState: ConnectionsState = {
  connections: {},
  status: {},
  content: {},
};

function enhanceDatasourceItem(
  item: ContentItems,
  columns: ColumnSchema[]
): ContentItems {
  const enhancedItem: ContentItems = {};
  // Datasources response items enhancing
  for (const [key, itemData] of Object.entries(item)) {
    const column = columns.find((c) => c.key === key);
    if (!column) {
      enhancedItem[key] = {
        type: 'string' as DatasourceType['type'],
        text: itemData.data?.toString() ?? '',
        data: itemData.data,
      };
      continue;
    }

    enhancedItem[key] = {
      type: column.type as DatasourceType['type'],
      text: getTextFromDatasourceValue(column, item) as string,
      data: itemData.data,
    };
  }
  return enhancedItem;
}

export const connectionsSlice = createSlice({
  name: 'connections',
  initialState,
  reducers: {
    loadContentPending: (state, action: PayloadAction<{ ari: string }>) => {
      const { ari } = action.payload;
      state.status[ari] = { content: { status: AsyncOperationStatus.PENDING } };
    },
    loadContentFinished: (
      state,
      action: PayloadAction<{
        connectionId: string;
        ari: string;
        access: Access;
      }>
    ) => {
      const { connectionId, ari, access } = action.payload;
      state.status[ari] = {
        content: { status: AsyncOperationStatus.SUCCEEDED },
      };
      state.connections[connectionId].meta = { access };
    },
    loadContentError: (
      state,
      action: PayloadAction<{
        ari: string;
        error: SerializedError;
      }>
    ) => {
      const { ari, error } = action.payload;
      state.status[ari] = {
        content: { status: AsyncOperationStatus.ERROR, error },
      };
    },
    loadContentColumnPending: (
      state,
      action: PayloadAction<{ ari: string; column: string }>
    ) => {
      const { ari, column } = action.payload;
      if (!state.status[ari]) {
        state.status[ari] = {
          content: { status: AsyncOperationStatus.INITIAL },
        };
      }

      if (!state.status[ari].columns) {
        state.status[ari].columns = {};
      }

      state.status[ari] = {
        ...state.status[ari],
        columns: {
          ...state.status[ari].columns,
          [column]: { status: AsyncOperationStatus.PENDING },
        },
      };
    },
    loadContentColumnFinished: (
      state,
      action: PayloadAction<{ ari: string; column: string }>
    ) => {
      const { ari, column } = action.payload;
      if (!state.status[ari]) {
        state.status[ari] = {
          content: { status: AsyncOperationStatus.INITIAL },
        };
      }

      if (!state.status[ari].columns) {
        state.status[ari].columns = {};
      }

      state.status[ari] = {
        ...state.status[ari],
        columns: {
          ...state.status[ari].columns,
          [column]: { status: AsyncOperationStatus.SUCCEEDED },
        },
      };
    },
    loadContentColumnError: (
      state,
      action: PayloadAction<{
        ari: string;
        column: string;
        error: SerializedError;
      }>
    ) => {
      const { ari, error, column } = action.payload;
      if (!state.status[ari]) {
        state.status[ari] = {
          content: { status: AsyncOperationStatus.INITIAL },
        };
      }

      state.status[ari] = {
        ...state.status[ari],
        columns: {
          ...state.status[ari].columns,
          [column]: {
            status: AsyncOperationStatus.ERROR,
            error,
          },
        },
      };
    },
    addConnection: (
      state,
      action: PayloadAction<{
        cloudId: string;
        connectionId: string;
        requiredColumns: string[];
        type: ConnectionType;
      }>
    ) => {
      const { cloudId, connectionId, requiredColumns, type } = action.payload;
      if (
        !state.connections[connectionId] ||
        state.connections[connectionId].type !== type
      ) {
        state.connections[connectionId] = {
          cloudId,
          type,
          requiredColumns,
          columns: {},
          content: [],
          meta: undefined,
        };
      }
    },
    addRequiredColumnToConnection: (
      state,
      action: PayloadAction<{ connectionId: string; column: string }>
    ) => {
      const { connectionId, column } = action.payload;

      if (state.connections[connectionId]) {
        state.connections[connectionId].requiredColumns = Array.from(
          new Set([...state.connections[connectionId].requiredColumns, column])
        );
      }
    },
    addColumnToConnection: (
      state,
      action: PayloadAction<{
        connectionId: string;
        column: ColumnSchema;
      }>
    ) => {
      const { connectionId, column } = action.payload;
      if (state.connections[connectionId]) {
        state.connections[connectionId].columns[column.key] = column;
      }
    },
    addColumnValueToContent: (
      state,
      action: PayloadAction<{
        ari: string;
        columnName: string;
        data: {
          type: FieldValueTypes['type'];
          text: string;
          value: FieldValueTypes['value'] | FieldValueTypes['value'][];
        };
      }>
    ) => {
      const { ari, columnName, data } = action.payload;
      const content = state.content[ari];
      // Add item to content only of column exists in the connection
      // Commented out because of the lack of pageStatus loading when a page status field is added
      // if (!state.connections[content.connectionId].columns[columnName]) {
      //   return;
      // }

      if (content) {
        content.item[columnName] = {
          type: data.type,
          text: data.text,
          data: data.value,
        };
      }
    },
    removeColumnValueFromContent: (
      state,
      action: PayloadAction<{
        ari: string;
        columnName: string;
      }>
    ) => {
      const { ari, columnName } = action.payload;
      const content = state.content[ari];

      if (content) {
        delete content.item[columnName];
      }
    },
    addContentToList: (
      state,
      action: PayloadAction<{
        ari: string;
        connectionId: string;
        item: ContentItems;
        columns: ColumnSchema[];
      }>
    ) => {
      const { ari, connectionId, item, columns } = action.payload;

      // Add content to the store
      state.content[ari] = {
        connectionId,
        ari,
        item: enhanceDatasourceItem(item, columns),
      };

      // Add content ari to connection content list
      if (!state.connections[connectionId].content.includes(ari)) {
        state.connections[connectionId].content.push(ari);
      }

      // Add columns to the connection
      columns.forEach((column) => {
        state.connections[connectionId].columns[column.key] = column;
      });
    },
    resetConnection(state, action: PayloadAction<{ connectionId: string }>) {
      const { connectionId } = action.payload;
      delete state.connections[connectionId];
    },
  },
});

export default connectionsSlice;
